import { useEffect, useState, type ChangeEvent, type FC, type KeyboardEvent } from 'react';
import type { Editor } from '@tiptap/react';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
import { useClickOutside } from '@/js/libs/use-click-outside/index.tsx';

import Icons from '../../Elements/Icons/index.ts';
import { Input } from '../Input/index.tsx';
import { getTestId } from './helpers.ts';
import { Divider, Link, LinkTooltipWrapper, ToolbarItem } from './styled.ts';

interface LinkTooltipProps {
  editor: Editor;
  saveLink: (href: string) => void;
  isCreating?: boolean;
  placeholder?: string;
  dataTestId?: string;
}

export const LinkTooltip: FC<LinkTooltipProps> = ({ editor, saveLink, isCreating, placeholder, dataTestId }) => {
  const href = editor.getAttributes('link').href ?? '';
  const [value, setValue] = useState<string>(href);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [wrapperRef, hasClickedOutside] = useClickOutside<HTMLDivElement>();

  const handleValueChange = useCallbackRef((e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  });

  const handleKeyUp = useCallbackRef((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveLink(value);
      setIsEditing(false);
    }
  });

  const handleSaveClick = useCallbackRef(() => {
    saveLink(value);
    setIsEditing(false);
  });

  const handleEditClick = useCallbackRef(() => {
    setValue(href);
    setIsEditing(true);
  });

  const handleDeleteClick = useCallbackRef(() => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
  });

  useEffect(() => {
    if (hasClickedOutside) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsEditing(false);
    }
  }, [hasClickedOutside]);

  return (
    <LinkTooltipWrapper ref={wrapperRef} data-testid={getTestId('link-tooltip', dataTestId)}>
      {isCreating || isEditing ? (
        <>
          <Input
            type='text'
            value={value}
            onChange={handleValueChange}
            onKeyUp={handleKeyUp}
            placeholder={placeholder}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            data-testid={getTestId('link-input', dataTestId)}
          />
          <ToolbarItem type='button' $withPadding onClick={handleSaveClick} data-testid={getTestId('link-save', dataTestId)}>
            <Icons.Checkmark size={20} />
          </ToolbarItem>
        </>
      ) : (
        <>
          <Link to={href} target='_blank' data-testid={getTestId('link-display', dataTestId)}>
            {href}
          </Link>
          <Divider />
          <ToolbarItem type='button' onClick={handleEditClick} data-testid={getTestId('link-edit', dataTestId)}>
            <Icons.Edit size={20} />
          </ToolbarItem>
          <ToolbarItem type='button' onClick={handleDeleteClick} data-testid={getTestId('link-delete', dataTestId)}>
            <Icons.Delete size={20} />
          </ToolbarItem>
        </>
      )}
    </LinkTooltipWrapper>
  );
};
